// File: src/components/ui/ScheduleCard/ScheduleCard.tsx

import React from "react";
import styled, { css } from "styled-components";
import { Flex } from "../../layouts/wrappers";
import { Typography } from "../../general/Typography";

export interface ScheduleCardProps {
  time: string;
  title: string;
  subtitle?: string;
  status?: "in-progress" | "upcoming" | "done";
  onClick?: () => void;
  icon?: React.ReactNode;
}

const CardWrapper = styled(Flex).attrs(() => ({
  direction: "row",
  align: "flex-start",
}))<{ clickable?: boolean }>`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.radii.lg};
  box-shadow: ${({ theme }) => theme.elevations.low};
  cursor: ${({ clickable }) => (clickable ? "pointer" : "default")};
  transition: all 0.2s ease;
  flex-wrap: wrap;

  &:hover {
    box-shadow: ${({ theme }) => theme.elevations.md};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.spacing.sm};
    flex-direction: column;
    align-items: flex-start;
    gap: ${({ theme }) => theme.spacing.sm};
  }
`;

const TimeWrapper = styled.div`
  min-width: 64px;
  margin-right: ${({ theme }) => theme.spacing.sm};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    margin-right: 0;
  }
`;

const StatusPill = styled.span<{ status?: string }>`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: capitalize;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-top: ${({ theme }) => theme.spacing.xs};
  width: fit-content;

  ${({ status, theme }) => {
    switch (status) {
      case "in-progress":
        return css`background: ${theme.colors.info};`;
      case "upcoming":
        return css`background: ${theme.colors.warning};`;
      case "done":
        return css`background: ${theme.colors.success};`;
      default:
        return css`background: ${theme.colors.border};`;
    }
  }}
`;

const IconWrapper = styled.div`
  margin-left: auto;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    margin-left: 0;
    margin-top: ${({ theme }) => theme.spacing.xs};
  }
`;

export const ScheduleCard: React.FC<ScheduleCardProps> = ({
  time,
  title,
  subtitle,
  status,
  onClick,
  icon,
}) => {
  return (
    <CardWrapper fullWidth gap="sm" clickable={!!onClick} onClick={onClick}>
      <TimeWrapper>
        <Typography variant="bodySmall" as="span">
          {time}
        </Typography>
      </TimeWrapper>

      <Flex direction="column" gap="xxs" flex="1" fullWidth>
        <Typography variant="body" weight="medium" as="span">
          {title}
        </Typography>

        {subtitle && (
          <Typography variant="caption" colorScheme="muted" as="span">
            {subtitle}
          </Typography>
        )}

        {status && <StatusPill status={status}>{status}</StatusPill>}
      </Flex>

      {icon && <IconWrapper>{icon}</IconWrapper>}
    </CardWrapper>
  );
};